import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useApp } from "@/context/AppContext";
import { Bell, Inbox, Check, Sparkles, HelpCircle, AlertCircle, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

const KIND_MAP = {
    question: { icon: HelpCircle, color: "bg-warning/15 text-warning-foreground border-warning/30" },
    success:  { icon: Check,      color: "bg-success/15 text-success border-success/30" },
    error:    { icon: AlertCircle, color: "bg-destructive/10 text-destructive border-destructive/30" },
    info:     { icon: Info,       color: "bg-primary/10 text-primary border-primary/30" },
};

export default function Notifications() {
    const { notifications, unread, markReadAll, markRead, refreshNotifications } = useApp();

    useEffect(() => { refreshNotifications(); /* eslint-disable-next-line */ }, []);

    if (!notifications.length) {
        return (
            <div className="px-5 sm:px-8 py-16 max-w-3xl mx-auto">
                <Card className="p-12 text-center border-dashed border-border" data-testid="notifications-empty">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary mx-auto"><Inbox className="h-6 w-6 text-muted-foreground" /></span>
                    <h2 className="mt-5 font-serif text-3xl">Inbox is clear</h2>
                    <p className="mt-3 text-muted-foreground max-w-md mx-auto">When Owed finishes a scan, has a question for you, or completes a packet, it'll show up here.</p>
                </Card>
            </div>
        );
    }

    return (
        <div className="px-5 sm:px-8 py-8 lg:py-10 max-w-4xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Inbox</p>
                    <h1 className="mt-2 font-serif text-4xl sm:text-5xl leading-tight">Notifications {unread > 0 && <span className="text-muted-foreground font-normal text-2xl align-middle">· {unread} unread</span>}</h1>
                </div>
                {unread > 0 && (
                    <Button onClick={markReadAll} variant="outline" className="rounded-full" data-testid="notifications-mark-all-read-btn">
                        <Check className="h-4 w-4 mr-1" /> Mark all read
                    </Button>
                )}
            </div>

            <div className="mt-8 space-y-3">
                {notifications.map((n) => {
                    const cfg = KIND_MAP[n.kind] || KIND_MAP.info;
                    const Icon = cfg.icon;
                    const ago = safeAgo(n.created_at);
                    const appId = n.meta?.application_id;
                    return (
                        <Card key={n.id} className={cn("p-5 border transition-colors flex items-start gap-4", !n.read && "bg-secondary/40")} data-testid={`notification-${n.id}`}>
                            <span className={cn("h-10 w-10 rounded-lg border inline-flex items-center justify-center shrink-0", cfg.color)}>
                                <Icon className="h-5 w-5" />
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-serif text-lg leading-tight">{n.title}</p>
                                    {!n.read && <Badge variant="outline" className="bg-accent/20 border-accent/30 text-accent-foreground text-[10px]">NEW</Badge>}
                                </div>
                                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{n.body}</p>
                                <div className="mt-3 flex items-center gap-3">
                                    <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{ago}</p>
                                    {appId && (
                                        <Button asChild size="sm" variant="ghost" className="h-7 text-xs">
                                            <Link to={`/app/applications`} onClick={() => markRead([n.id])}>Open application →</Link>
                                        </Button>
                                    )}
                                    {!n.read && (
                                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => markRead([n.id])} data-testid={`notification-mark-read-${n.id}`}>Mark read</Button>
                                    )}
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
}

function safeAgo(iso) {
    try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
    catch { return "recently"; }
}
